import { createSlice } from "@reduxjs/toolkit";

export type UserActivePositionProps = {
  positionId: string;
  wantAmount: string;
  reserveAmount: string;
  stopLossLowerPrice: string;
  stopLossUpperPrice: string;
  positionValue: string;
  debtValue: string;
  lowerTick: string;
  upperTick: string;
  fee: string;
  healthFactor: string;
  upperTickPrice: string;
  lowerTickPrice: string;
  entryPrice: string;
  borrowId: string;
  principal: string;
  borrowRatio: string;
  reserveAmountAtStart: number;
  positionCreateTimestamp: number;
  wantTokenFee: number;
  borrowTokenFee: number;
  positionValueInWantToken: string;
};

export type UserClosedPositionProps = {
  positionId: number;
  startWantAmount: string;
  endWantAmount: string;
  reserveAmount: string;
  closePrice: string;
  eventName: string;
  timestamp: number;
  startPrice: number;
  startTime: number;
  closeTime: number;
  upperTickPrice: number;
  lowerTickPrice: number;
  borrowRatio: number;
  reserveWantAmount: number;
};

type FactoryProps = {
  ethPrice: number;
  volumeIn24H: number;
  feeIn24H: number;
  tvl: number;
  accountBalance: number;
  userActivePositions: {
    [key: number]: { [key: string]: UserActivePositionProps[] };
  };
  userClosedPositions: {
    [key: number]: {
      [key: string]: UserClosedPositionProps[];
    };
  };
  positionInfos: {
    [key: string]: { [key: string]: { [key: string]: number | string } };
  };
  closedPositions: {
    [key: number]: {
      records: [];
      totalRecord: number;
    };
  };
  closedPositionIsFetched: boolean;
  activePositions: {
    [key: number]: {
      records: [];
      totalRecord: number;
    };
  };
  activePositionIsFetched: boolean;
  totalUsdcInFactory: number;
  uniPoolStats: {
    [key: string]: {
      volumeIn24H: number;
      feeIn24H: number;
      tvl: number;
    };
  };
  unibotTvl: { [key: number]: number };
  unibotCumulativeFee: { [key: number]: number };
};

const initialState: FactoryProps = {
  ethPrice: 0,
  volumeIn24H: 0,
  feeIn24H: 0,
  tvl: 0,
  userActivePositions: {},
  accountBalance: 0,
  userClosedPositions: {},
  positionInfos: {},
  closedPositions: {},
  closedPositionIsFetched: false,
  activePositions: {},
  activePositionIsFetched: false,
  totalUsdcInFactory: 0,
  uniPoolStats: {},
  unibotTvl: {},
  unibotCumulativeFee: {},
};

const factorySlice = createSlice({
  name: "factory",
  initialState: initialState,
  reducers: {
    SET_ETH_TOKEN_PRICE(state, action) {
      state.ethPrice = action.payload;
    },
    SET_USER_ACTIVE_POSITIONS(state, action) {
      const { factoryAddress, positionInfos, chainId } = action.payload;
      state.userActivePositions = {
        ...state.userActivePositions,
        [chainId]: {
          ...state.userActivePositions[chainId],
          [factoryAddress]: positionInfos,
        },
      };
    },
    SET_ACCOUNT_BALANCE(state, action) {
      state.accountBalance = action.payload;
    },
    SET_USER_CLOSED_POSITIONS(state, action) {
      const { factoryAddress, positions, chainId } = action.payload;
      state.userClosedPositions = {
        ...state.userClosedPositions,
        [chainId]: {
          ...state.userClosedPositions[chainId],
          [factoryAddress]: positions,
        },
      };
    },
    GET_OPEN_POSITION_INFO(state, action) {
      const { factoryAddress, positionInfo } = action.payload;
      state.positionInfos = {
        ...state.positionInfos,
        [factoryAddress]: positionInfo,
      };
    },
    setClosedPositionIsFetched(state, { payload }) {
      state.closedPositionIsFetched = payload;
    },
    SET_CLOSED_POSITIONS(state, action) {
      const { chainId, records, totalRecord } = action.payload;
      state.closedPositions = {
        ...state.closedPositions,
        [chainId]: {
          records: records,
          totalRecord: totalRecord,
        },
      };
    },
    setActivePositionIsFetched(state, { payload }) {
      state.activePositionIsFetched = payload;
    },
    SET_ACTIVE_POSITIONS(state, action) {
      const { chainId, records, totalRecord } = action.payload;
      state.activePositions = {
        ...state.activePositions,
        [chainId]: {
          records: records,
          totalRecord: totalRecord,
        },
      };
    },
    SET_TOTAL_USDC_IN_FACTORY(state, action) {
      state.totalUsdcInFactory = action.payload;
    },
    SET_UNI_POOL_24H_VOLUME(state, action) {
      const { poolAddress, volume } = action.payload;
      state.uniPoolStats = {
        ...state.uniPoolStats,
        [poolAddress]: {
          ...state.uniPoolStats[poolAddress],
          volumeIn24H: volume,
        },
      };
    },
    SET_UNI_POOL_24H_FEE(state, action) {
      const { poolAddress, fee } = action.payload;
      state.uniPoolStats = {
        ...state.uniPoolStats,
        [poolAddress]: {
          ...state.uniPoolStats[poolAddress],
          feeIn24H: fee,
        },
      };
    },
    SET_UNI_POOL_TVL(state, action) {
      const { poolAddress, tvl } = action.payload;
      state.uniPoolStats = {
        ...state.uniPoolStats,
        [poolAddress]: {
          ...state.uniPoolStats[poolAddress],
          tvl: tvl,
        },
      };
    },
    CLEAR_USER_POSITIONS(state) {
      state.userClosedPositions = {};
      state.userActivePositions = {};
    },
    updateUnibotTvl(state, { payload }) {
      const { chainId, tvl } = payload;
      state.unibotTvl = {
        ...state.unibotTvl,
        [chainId]: tvl,
      };
    },
    updateUnibotCumulativeFee(state, { payload }) {
      const { chainId, cumulativeFee } = payload;
      state.unibotCumulativeFee = {
        ...state.unibotCumulativeFee,
        [chainId]: cumulativeFee,
      };
    },
  },
});

export const {
  SET_ETH_TOKEN_PRICE,
  SET_USER_ACTIVE_POSITIONS,
  SET_ACCOUNT_BALANCE,
  SET_USER_CLOSED_POSITIONS,
  GET_OPEN_POSITION_INFO,
  SET_CLOSED_POSITIONS,
  SET_ACTIVE_POSITIONS,
  SET_TOTAL_USDC_IN_FACTORY,
  SET_UNI_POOL_24H_VOLUME,
  SET_UNI_POOL_24H_FEE,
  SET_UNI_POOL_TVL,
  CLEAR_USER_POSITIONS,
  setClosedPositionIsFetched,
  setActivePositionIsFetched,
  updateUnibotTvl,
  updateUnibotCumulativeFee,
} = factorySlice.actions;

export default factorySlice.reducer;
